"use client";

import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

const SCENARIOS = [
  {
    id: "realestate",
    label: "Real Estate",
    icon: "🏠",
    outcome: "Lead qualified & showing booked",
    outcomeColor: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
    transcript: [
      { role: "caller", text: "Hi, I saw your listing on Zillow — the 3-bed on Maple Street. Is it still available?" },
      { role: "agent", text: "Yes, it's still active! Great choice — that property just had a price improvement. Are you looking to buy, or are you also considering selling your current home?" },
      { role: "caller", text: "Just buying. We're hoping to move within the next 3 months." },
      { role: "agent", text: "That's a great timeline. Have you been pre-approved for financing, or would you like us to connect you with our preferred lender first?" },
      { role: "caller", text: "We're pre-approved up to $650k." },
      { role: "agent", text: "That puts you well within range for this property — and a few others in the area I think you'd love. Can I schedule a showing with one of our agents this weekend?" },
      { role: "caller", text: "Saturday afternoon works for us." },
      { role: "agent", text: "Confirmed — Saturday at 2 PM. Our agent Sarah will call you Friday to confirm. I've also sent a summary of three similar listings to your number. Anything else you'd like to know?" },
    ],
  },
  {
    id: "clinic",
    label: "Medical Clinic",
    icon: "🏥",
    outcome: "Appointment booked in 90 seconds",
    outcomeColor: "text-blue-400 bg-blue-500/10 border-blue-500/20",
    transcript: [
      { role: "caller", text: "Hi, I need to schedule an appointment with Dr. Chen." },
      { role: "agent", text: "Of course! I can help with that. Are you an existing patient?" },
      { role: "caller", text: "Yes, I've been a patient for about two years." },
      { role: "agent", text: "Great. Dr. Chen has availability this Thursday at 10 AM or Friday at 2 PM. Which works better for you?" },
      { role: "caller", text: "Thursday at 10 works perfectly." },
      { role: "agent", text: "Perfect — I've booked you in for Thursday at 10 AM with Dr. Chen. You'll receive a confirmation text shortly. Is there anything else I can help with?" },
      { role: "caller", text: "No, that's all. Thank you!" },
      { role: "agent", text: "You're welcome. See you Thursday!" },
    ],
  },
  {
    id: "law",
    label: "Law Firm",
    icon: "⚖️",
    outcome: "Consultation scheduled & case qualified",
    outcomeColor: "text-amber-400 bg-amber-500/10 border-amber-500/20",
    transcript: [
      { role: "caller", text: "Hello, I'd like to speak with someone about a personal injury case." },
      { role: "agent", text: "I can help you get connected with the right attorney. Can I ask — did the incident occur in the last two years?" },
      { role: "caller", text: "Yes, it was about six months ago." },
      { role: "agent", text: "Thank you. Our personal injury team handles cases exactly like yours. I'll schedule a free 30-minute consultation. Are you available any time this week?" },
      { role: "caller", text: "Wednesday afternoon would be ideal." },
      { role: "agent", text: "Wednesday at 3 PM is open. I've reserved that slot for you — an attorney will call you directly. I'll also take down a brief description of the incident to prepare them in advance." },
    ],
  },
  {
    id: "salon",
    label: "Salon & Spa",
    icon: "✂️",
    outcome: "Booking confirmed with preferred stylist",
    outcomeColor: "text-pink-400 bg-pink-500/10 border-pink-500/20",
    transcript: [
      { role: "caller", text: "Hi, I'd like to book a haircut and colour for Saturday." },
      { role: "agent", text: "Lovely! For a cut and colour, we recommend a 2.5-hour slot. We have Saturday at 9 AM or 1 PM available. Which would you prefer?" },
      { role: "caller", text: "1 PM please." },
      { role: "agent", text: "Wonderful! Shall I assign you to Emma, your regular stylist, or would you like to try someone new?" },
      { role: "caller", text: "Emma, please." },
      { role: "agent", text: "Done — Saturday 1 PM with Emma. Confirmation sent to your number. We'll send a reminder Friday morning. See you Saturday!" },
    ],
  },
];

function AgentIcon() {
  return (
    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center text-white text-xs font-bold shadow-md shadow-violet-500/30 flex-shrink-0">
      AI
    </div>
  );
}

function CallerIcon() {
  return (
    <div className="w-8 h-8 rounded-full bg-zinc-700 border border-white/10 flex items-center justify-center text-zinc-400 text-xs flex-shrink-0">
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
        <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
      </svg>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex gap-3 items-start">
      <AgentIcon />
      <div className="bg-violet-500/15 border border-violet-500/20 rounded-2xl rounded-tl-none px-4 py-3">
        <div className="flex gap-1 items-center">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="w-1.5 h-1.5 rounded-full bg-violet-400"
              animate={{ opacity: [0.3, 1, 0.3], y: [0, -3, 0] }}
              transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.2 }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function TranscriptMessage({ message, visible }) {
  if (!visible) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className={`flex gap-3 items-start ${message.role === "agent" ? "" : "flex-row-reverse"}`}
    >
      {message.role === "agent" ? <AgentIcon /> : <CallerIcon />}
      <div
        className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
          message.role === "agent"
            ? "bg-violet-500/15 border border-violet-500/20 text-zinc-200 rounded-tl-none"
            : "bg-zinc-800 border border-white/5 text-zinc-300 rounded-tr-none"
        }`}
      >
        {message.text}
      </div>
    </motion.div>
  );
}

export default function VoiceAgentDemo() {
  const [activeScenario, setActiveScenario] = useState(0);
  const [visibleCount, setVisibleCount] = useState(0);
  const [showTyping, setShowTyping] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasAutoPlayed, setHasAutoPlayed] = useState(false);
  const intervalRef = useRef(null);
  const transcriptRef = useRef(null);
  const sectionRef = useRef(null);

  const scenario = SCENARIOS[activeScenario];

  function runPlayback(startFrom = 0, scenarioIdx = activeScenario) {
    clearInterval(intervalRef.current);
    setIsPlaying(true);
    setShowTyping(false);
    setVisibleCount(startFrom);

    let count = startFrom;
    const transcript = SCENARIOS[scenarioIdx].transcript;

    function showNext() {
      if (count >= transcript.length) {
        setShowTyping(false);
        setIsPlaying(false);
        return;
      }
      const msg = transcript[count];
      if (msg.role === "agent") {
        setShowTyping(true);
        setTimeout(() => {
          setShowTyping(false);
          count++;
          setVisibleCount(count);
          intervalRef.current = setTimeout(showNext, 900);
        }, 900);
      } else {
        count++;
        setVisibleCount(count);
        intervalRef.current = setTimeout(showNext, 1100);
      }
    }

    intervalRef.current = setTimeout(showNext, 400);
  }

  function handleScenarioChange(idx) {
    clearInterval(intervalRef.current);
    setActiveScenario(idx);
    setIsPlaying(false);
    setShowTyping(false);
    setVisibleCount(0);
    // Auto-play the new scenario after state settles
    setTimeout(() => runPlayback(0, idx), 300);
  }

  // Auto-play once when section scrolls into view
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAutoPlayed) {
          setHasAutoPlayed(true);
          setTimeout(() => runPlayback(0, 0), 600);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasAutoPlayed]);

  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [visibleCount, showTyping]);

  return (
    <section ref={sectionRef} className="bg-zinc-950 py-24 border-t border-white/[0.04]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-300 text-sm font-medium mb-4">
            <motion.span
              className="w-1.5 h-1.5 rounded-full bg-violet-400 inline-block"
              animate={{ opacity: [1, 0.4, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
            Live demo
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            See it handle a real call
          </h2>
          <p className="text-zinc-400 text-lg max-w-xl mx-auto">
            Pick an industry and watch how the agent qualifies, books, and converts — without a human on the line.
          </p>
        </div>

        {/* Scenario tabs */}
        <div className="flex justify-center gap-2 mb-8 flex-wrap">
          {SCENARIOS.map((s, i) => (
            <button
              key={s.id}
              onClick={() => handleScenarioChange(i)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                activeScenario === i
                  ? "bg-violet-600 text-white shadow-lg shadow-violet-500/25"
                  : "bg-zinc-800/80 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200 border border-white/5"
              }`}
            >
              <span>{s.icon}</span>
              {s.label}
            </button>
          ))}
        </div>

        {/* Chat window */}
        <div className="bg-zinc-900 rounded-2xl border border-white/[0.08] overflow-hidden shadow-2xl shadow-black/40">

          {/* Window chrome */}
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/[0.06] bg-zinc-900/90 backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500/70" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/70" />
                <div className="w-3 h-3 rounded-full bg-green-500/70" />
              </div>
              <span className="text-sm text-zinc-400 font-medium">
                Asynq Voice Agent
                <span className="text-zinc-600 mx-1.5">·</span>
                <span className="text-zinc-500">{scenario.label}</span>
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <motion.span
                className="w-2 h-2 rounded-full bg-green-400"
                animate={{ opacity: [1, 0.4, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
              <span className="text-xs text-green-400 font-medium">Connected</span>
            </div>
          </div>

          {/* Transcript */}
          <div
            ref={transcriptRef}
            className="p-6 space-y-4 min-h-[340px] max-h-[420px] overflow-y-auto scroll-smooth"
          >
            {scenario.transcript.map((message, i) => (
              <TranscriptMessage
                key={`${activeScenario}-${i}`}
                message={message}
                visible={i < visibleCount}
              />
            ))}
            {showTyping && <TypingIndicator />}
          </div>

          {/* Outcome tag + footer */}
          <div className="px-6 py-4 border-t border-white/[0.06] flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border ${scenario.outcomeColor}`}>
                <svg className="w-3 h-3" viewBox="0 0 12 12" fill="currentColor">
                  <path fillRule="evenodd" d="M10.22 3.22a.75.75 0 0 1 0 1.06l-5.25 5.25a.75.75 0 0 1-1.06 0L1.78 7.4a.75.75 0 0 1 1.06-1.06l1.6 1.6 4.72-4.72a.75.75 0 0 1 1.06 0Z" clipRule="evenodd" />
                </svg>
                {scenario.outcome}
              </span>
              <span className="text-xs text-zinc-600">· Response latency &lt; 800ms</span>
            </div>
            <button
              onClick={() => runPlayback(0)}
              disabled={isPlaying}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-violet-600 hover:bg-violet-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold transition-all shrink-0"
            >
              {isPlaying ? (
                <>
                  <motion.span
                    className="w-1.5 h-1.5 rounded-full bg-white"
                    animate={{ opacity: [1, 0.3, 1] }}
                    transition={{ duration: 0.8, repeat: Infinity }}
                  />
                  Playing…
                </>
              ) : (
                <>
                  <svg className="w-3 h-3" viewBox="0 0 12 12" fill="currentColor">
                    <path d="M2 2l8 4-8 4V2z" />
                  </svg>
                  Replay
                </>
              )}
            </button>
          </div>
        </div>

        <p className="text-center text-zinc-600 text-sm mt-6">
          Every conversation is trained on your business — your services, your tone, your workflows.
        </p>
      </div>
    </section>
  );
}
